import * as _ from 'lodash';
import * as React from 'react';
import { observer } from 'mobx-react';
import { useTheme } from '@emotion/react';

import { MFAFactor } from '@journeyapps-platform/types-accounts-hub';

import { Theme, styled } from '../../../../theme';
import { useMFAStore } from '../../../../hooks/useMFAStore';
import { ConfigurableCardWidget } from '../../../../widgets/card/ConfigurableCardWidget';
import { Btn, ButtonType } from '../../../../widgets/buttons/ButtonWidget';
import { useDialogStore } from '../../../../hooks/useDialogService';
import { wrapLoadingAction } from '../../../../utils/actions';
import { PillWidget } from '../../../../widgets/PillWidget';
import { CenterFormOrientation, CenterFormWidget } from '../../../../widgets/layout/CenterFormWidget';
import { FormLayoutWidget } from '../../../../widgets/layout/FormLayoutWidget';
import { GroupedButtons } from '../../../../widgets/buttons/GroupedButtons';
import { MFAProviderIconWidget } from './MFAProviderIconWidget';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MFAFormContent } from './SharedMFADialogComponents';
import { SmartGenerateBackupCodesWidget } from '../../../../widgets-smart/SmartGenerateBackupCodesWidget';

export type MFASelectorWidgetProps = {
  onConfigure: (selected: MFAFactor) => Promise<void>;
  onCancel: () => void;
};

namespace S {
  export const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  `;

  export const RowContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-around;
  `;

  export const ConfigurableCard = styled(ConfigurableCardWidget)`
    min-width: 170px;
  `;
}

export const MFAProviderSelectorWidget: React.FC<MFASelectorWidgetProps> = observer((props) => {
  const { onConfigure } = props;

  const mfaStore = useMFAStore();
  const dialogStore = useDialogStore();
  const theme = useTheme() as Theme;

  const factorDescriptions = {
    [MFAFactor.SMS]: {
      title: 'SMS/Text',
      description: 'less secure',
      icon: () => <FontAwesomeIcon icon="comment-sms" />
    },
    [MFAFactor.TOTP]: {
      title: 'Authenticator App',
      description: 'more secure',
      icon: () => <FontAwesomeIcon icon="mobile-screen" />
    }
  };

  return (
    <CenterFormWidget noLogo={true} primaryLabel={'Configure MFA'} orientation={CenterFormOrientation.ROW}>
      <FormLayoutWidget
        sections={[
          {
            key: 'content',
            elements: [
              <MFAFormContent key="content">
                <S.Container>
                  <S.RowContainer>
                    {_.values(MFAFactor)
                      .reverse()
                      .map((factorType) => {
                        const selectorProps = factorDescriptions[factorType];

                        const factors = _.values(mfaStore.models);
                        const enabledFactor = factors.find((factorModel) => factorModel.factorType == factorType);
                        const isPreferred = mfaStore.preferredMFAFactor?.factorType == factorType;
                        const secondaryActions: Btn[] = [];
                        if (enabledFactor) {
                          secondaryActions.push({
                            label: 'Remove configuration',
                            action: async (event, loading) =>
                              wrapLoadingAction(
                                async () => {
                                  /**
                                   * Don't allow the user to delete the active factor if there are other factors configured
                                   * already. They first have to select the other as active.
                                   */
                                  if (
                                    isPreferred &&
                                    !!factors.find((factorModel) => factorModel.factorType != factorType)
                                  ) {
                                    return dialogStore.errorDialog({
                                      errorMessage: 'Cannot remove primary method. Set another method as primary first.'
                                    });
                                  }
                                  if (!(await dialogStore.confirmDialog({ message: 'Permanently remove this?' }))) {
                                    return;
                                  }
                                  const existingMFAFactors = _.values(mfaStore.models).filter(
                                    (factorModel) => factorModel.factorType == factorType
                                  );

                                  for (let staleFactor of existingMFAFactors) {
                                    await staleFactor.delete();
                                  }
                                  await mfaStore.loadUserMFAConfig();
                                },
                                loading,
                                (error) => {
                                  console.error(error);
                                  return dialogStore.errorDialog({
                                    errorMessage: 'Could not remove existing configuration.'
                                  });
                                }
                              )
                          });

                          if (!isPreferred) {
                            secondaryActions.push({
                              label: 'Set as primary',
                              action: async (event, loading) =>
                                wrapLoadingAction(
                                  async () => {
                                    await enabledFactor.setAsPreferred();
                                    await mfaStore.loadUserMFAConfig();
                                  },
                                  loading,
                                  (error) => {
                                    console.error(error);
                                    return dialogStore.errorDialog({
                                      errorMessage: 'Could not set as primary method.'
                                    });
                                  }
                                )
                            });
                          }
                        }

                        return (
                          <S.ConfigurableCard
                            key={factorType}
                            secondaryActions={secondaryActions}
                            meta={
                              isPreferred ? (
                                <PillWidget label="Primary" color="none" borderColor={theme.pills.border} />
                              ) : null
                            }
                            configured={!!enabledFactor}
                            onConfigure={(event, loading) =>
                              wrapLoadingAction(
                                async () => {
                                  await onConfigure(factorType as MFAFactor);
                                },
                                loading,
                                (error) => {
                                  console.error(error);
                                  return dialogStore.errorDialog({
                                    errorMessage:
                                      'Could not configure multi-factor authentication method. See console for details.'
                                  });
                                }
                              )
                            }
                            content={<MFAProviderIconWidget image={selectorProps.icon} />}
                            {...selectorProps}
                          />
                        );
                      })}
                  </S.RowContainer>
                  {mfaStore.preferredMFAFactor ? <SmartGenerateBackupCodesWidget showMessage={false} /> : null}
                </S.Container>
              </MFAFormContent>
            ]
          },
          {
            key: 'buttons',
            elements: [
              <GroupedButtons
                key="buttons"
                btns={[
                  {
                    label: 'Close',
                    type: ButtonType.TERTIARY,
                    action: () => {
                      props.onCancel();
                    }
                  }
                ]}
              />
            ]
          }
        ]}
      />
    </CenterFormWidget>
  );
});
